import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import React, { useCallback, useRef } from 'react';
import { PatternSegment } from '../../utils/PatternEngine';
import { Alert, Button, useTranslation } from '../blocks';
import { SegmentCard } from './SegmentCard';

const MAX_REGEX_LENGTH = 80;

export interface PatternBuilderProps {
    segments: PatternSegment[];
    onSegmentsChange: (segments: PatternSegment[]) => void;
    generatedRegex: string;
}

export const PatternBuilder: React.FC<PatternBuilderProps> = ({ segments, onSegmentsChange, generatedRegex }) => {
    const { t } = useTranslation('regexPatternModal');
    const idCounterRef = useRef(0);

    const generateSegmentId = useCallback(() => {
        idCounterRef.current += 1;
        return `builder-seg-${idCounterRef.current}`;
    }, []);

    const handleAddSegment = useCallback(() => {
        const newSegment: PatternSegment = {
            id: generateSegmentId(),
            type: 'exactText',
            value: '',
        };
        onSegmentsChange([...segments, newSegment]);
    }, [segments, onSegmentsChange, generateSegmentId]);

    const handleUpdateSegment = useCallback(
        (index: number, updated: PatternSegment) => {
            const next = segments.map((seg, i) => (i === index ? updated : seg));
            onSegmentsChange(next);
        },
        [segments, onSegmentsChange]
    );

    const handleRemoveSegment = useCallback(
        (index: number) => {
            const next = segments.filter((_, i) => i !== index);
            onSegmentsChange(next);
        },
        [segments, onSegmentsChange]
    );

    const handleMoveUp = useCallback(
        (index: number) => {
            if (index <= 0) return;
            const next = [...segments];
            const temp = next[index - 1];
            next[index - 1] = next[index];
            next[index] = temp;
            onSegmentsChange(next);
        },
        [segments, onSegmentsChange]
    );

    const handleMoveDown = useCallback(
        (index: number) => {
            if (index >= segments.length - 1) return;
            const next = [...segments];
            const temp = next[index + 1];
            next[index + 1] = next[index];
            next[index] = temp;
            onSegmentsChange(next);
        },
        [segments, onSegmentsChange]
    );

    const regexTooLong = generatedRegex.length > MAX_REGEX_LENGTH;

    return (
        <Column spacing={'300'} data-testid={'pattern-builder'}>
            <Column spacing={'200'}>
                {segments.map((segment, index) => (
                    <SegmentCard
                        key={segment.id}
                        segment={segment}
                        index={index}
                        onUpdate={handleUpdateSegment}
                        onRemove={handleRemoveSegment}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        isFirst={index === 0}
                        isLast={index === segments.length - 1}
                    />
                ))}
            </Column>

            <Row>
                <Button type={'primary'} size={'small'} onClick={handleAddSegment} data-testid={'add-segment-button'}>
                    {t('addSegment-buttonLabel')}
                </Button>
            </Row>

            {regexTooLong && (
                <Alert type={'warning'} size={'small'} data-testid={'regex-length-warning'}>
                    {t('regexLengthWarning', {
                        current: String(generatedRegex.length),
                        max: String(MAX_REGEX_LENGTH),
                    })}
                </Alert>
            )}
        </Column>
    );
};
